import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { BellOff, BellRing } from 'lucide-react';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import { feedPath } from '../listings/countries';
import { BrandLoader } from '../brand/Brand';
import apiService from '../../api';

// The links in alert emails: /<country>/immobilier/alerte/confirmer|desactiver/<token>.
// The page posts the action itself, so an email scanner that opens the link changes nothing.
const ACTIONS = { confirmer: 'confirm', desactiver: 'deactivate' };

function AlertPage() {
  const { country, action, token } = useParams();
  const [alert, setAlert] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | not_found | error
  const [busy, setBusy] = useState(false);

  const run = (name) => {
    setBusy(true);
    return apiService
      .post(`/api/v1/listings/alerts/${token}/`, { action: name })
      .then((data) => { setAlert(data); setStatus('ok'); })
      .catch((err) => setStatus(err?.response?.status === 404 ? 'not_found' : 'error'))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    if (!ACTIONS[action]) { setStatus('not_found'); return; }
    run(ACTIONS[action]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, token]);

  const feed = alert?.feed_url || feedPath(country);
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>Alerte e-mail - Recouvro</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />
      <main className="flex-grow px-4 py-12 sm:py-20">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
          {status === 'loading' && <div className="flex justify-center"><BrandLoader /></div>}
          {status === 'not_found' && (
            <>
              <h1 className="text-xl font-bold text-gray-900">Lien invalide</h1>
              <p className="mt-2 text-sm text-gray-600">Ce lien d'alerte n'existe pas ou plus. Vous pouvez créer une nouvelle alerte depuis les annonces.</p>
            </>
          )}
          {status === 'error' && (
            <>
              <h1 className="text-xl font-bold text-gray-900">Une erreur est survenue</h1>
              <p className="mt-2 text-sm text-gray-600">Réessayez dans un instant.</p>
              <button type="button" onClick={() => run(ACTIONS[action])} className="mt-4 text-blue-700 font-semibold underline">Réessayer</button>
            </>
          )}
          {status === 'ok' && alert.active && (
            <>
              <BellRing size={40} className="mx-auto text-blue-600" />
              <h1 className="mt-3 text-xl font-bold text-gray-900">Alerte activée</h1>
              <p className="mt-2 text-sm text-gray-600">Vous recevrez à <strong>{alert.email}</strong> les nouvelles annonces pour :</p>
              <p className="mt-2 text-sm font-semibold text-gray-900 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">{alert.criteria}</p>
              <p className="mt-3 text-xs text-gray-500">Un e-mail après chaque collecte qui trouve du nouveau, jamais deux fois la même annonce.</p>
            </>
          )}
          {status === 'ok' && !alert.active && (
            <>
              <BellOff size={40} className="mx-auto text-gray-400" />
              <h1 className="mt-3 text-xl font-bold text-gray-900">Alerte désactivée</h1>
              <p className="mt-2 text-sm text-gray-600">Vous ne recevrez plus d'e-mails pour : <strong>{alert.criteria}</strong>.</p>
              <button
                type="button"
                disabled={busy}
                onClick={() => run('confirm')}
                className="mt-4 border border-gray-300 text-gray-700 rounded-lg px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                Réactiver l'alerte
              </button>
            </>
          )}
          {status !== 'loading' && (
            <Link to={feed} className="mt-6 inline-block bg-blue-600 text-white font-semibold rounded-lg px-5 py-2.5">Voir les annonces</Link>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default AlertPage;
